import { courseService } from '@/services/course.service'
import { reviewService } from '@/services/review.service'
import { CourseDetails, Duration } from '@/types/course.types'
import type { ReviewStats } from '@/types/review.types'
import { useAuth } from '@/contexts/AuthContext'
import { SkeletonLoader } from '@/components/ui/SkeletonLoader'
import {
  AlertCircle,
  ArrowLeft,
  Award,
  BarChart3,
  BookOpen,
  Brain,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  Clock,
  Code,
  Database,
  FileText,
  GraduationCap,

  PlayCircle,
  Shield,
  Star,
  Target,
  Timer,
  TrendingUp,
  Trophy,
  // Users,
  Video,
  Zap
} from 'lucide-react'
import ReviewsList from '@/components/reviews/ReviewsList'
import { ReviewType } from '@/types/review.types'
import { useCallback, useEffect, useState, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import apiClient from '@/services/api'
import { API_ENDPOINTS } from '@/config/api.config'
import axios from 'axios'
import { getLanguageName } from '@/constants/languageOptions'
import useHasPermission from '@/hooks/useHasPermission'
import { LEARNER_COURSES_PERMISSION } from '@/constants/constant-permissions'
import { ADMIN_COURSE_CATALOG, LEARNER_COURSES } from '@/constants/constant-routes'

interface MarkdownComponentProps {
  children?: React.ReactNode;
  href?: string;
}

interface CoursePreviewProps {
  isLearner?: boolean;
}

export default function CoursePreview({ isLearner = false }: CoursePreviewProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { userMode, user } = useAuth()

  // Permission guards
  const hasWritePermission = useHasPermission([LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE]);
  const canStartLearning = !isLearner || hasWritePermission;

  // Redirect learners without any permission to access-denied
  // Read sessionStorage directly to avoid race condition with useHasPermission's useState(false) initial value
  useEffect(() => {
    if (!isLearner) return;
    const permissions: string[] = JSON.parse(sessionStorage.getItem('permissions') || '[]');
    const hasPermission = permissions.some(p =>
      p === LEARNER_COURSES_PERMISSION.LEARNER_COURSES_READ ||
      p === LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE
    );
    if (!hasPermission) {
      navigate('/access-denied', { replace: true });
    }
  }, [isLearner, navigate]);

  // Get initial tab from URL query param or default to 'overview'
  const initialTab = searchParams.get('tab') || 'overview'

  const handleGoBack = () => {
    // Navigate back to admin or learner courses based on isLearner prop
    navigate(isLearner ? LEARNER_COURSES : ADMIN_COURSE_CATALOG)
  }

  const [courseData, setCourseData] = useState<CourseDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({})
  const [accessVerified, setAccessVerified] = useState(!isLearner) // Skip verification for admin
  const [accessDenied, setAccessDenied] = useState(false)
  const [reviewStats, setReviewStats] = useState<ReviewStats | null>(null)
  const [assessmentDuration, setAssessmentDuration] = useState<Duration | null>(null)
  const [assessmentsList, setAssessmentsList] = useState<any[]>([])
  const [moduleContents, setModuleContents] = useState<Record<string, any>>({})
  const accessCheckDone = useRef(false)

  // SCORM-imported courses are tagged 'scorm'; they have no AI-style assessment
  // overview / final project, so those sections are hidden for them.
  const isScormCourse = (courseData?.tags || []).includes('scorm') ||
    (courseData as any)?.source_type === 'scorm_imported'

  const deliveryType = courseData?.generation_settings?.content_delivery_type || (courseData as any)?.content_delivery_type;
  const isHtmlOrAudio = deliveryType === 'html' || deliveryType === 'html_with_audio';

  // Empty / 'null' / 'undefined' all count as "no image".
  const usableUrl = (...candidates: (string | undefined | null)[]): string => {
    for (const candidate of candidates) {
      const raw = (candidate || '').trim()
      if (raw && raw.toLowerCase() !== 'null' && raw.toLowerCase() !== 'undefined') return raw
    }
    return ''
  }

  const [bannerError, setBannerError] = useState(false)
  const [thumbnailError, setThumbnailError] = useState(false)

  const bannerUrl = usableUrl(courseData?.banner, courseData?.banner_image_url)
  const thumbnailUrl = usableUrl(courseData?.thumbnail, courseData?.thumbnail_image_url, bannerUrl)

  // Courses with no usable image (SCORM, and manual courses with nothing uploaded) show the
  // BookOpen placeholder; an uploaded image always wins.
  const hasBanner = !!bannerUrl && !bannerError
  const hasThumbnail = !!thumbnailUrl && !thumbnailError

  // Reset on URL change so a newly uploaded image renders without a page reload.
  useEffect(() => { setBannerError(false) }, [bannerUrl])
  useEffect(() => { setThumbnailError(false) }, [thumbnailUrl])

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'course-info', label: 'Course Info', icon: AlertCircle },
    { id: 'curriculum', label: 'Curriculum', icon: FileText },
    { id: 'learning-outcomes', label: 'Learning Outcomes', icon: Target },
    { id: 'reviews', label: 'Reviews', icon: Star }
  ]

  // Verify resource access before loading course details (only for learners)
  const verifyAccess = useCallback(async () => {
    if (!id || !isLearner || accessCheckDone.current) return

    accessCheckDone.current = true

    try {
      await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_RESOURCE_ACCESS, {
        course_id: id
      })
      setAccessVerified(true)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data?.error
        if (errorCode === 'ACCESS_004' || error.response?.status === 403) {
          setAccessDenied(true)
          return
        }
      }
      // For other errors, allow access and let the page handle it
      setAccessVerified(true)
    }
  }, [id, isLearner])

  const loadCourseDetails = useCallback(async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await courseService.getCourseById(id)
      
      if (response?.data?.data) {
        setCourseData(response.data.data)
      }
    } catch (error) {
      console.error('Error loading course:', error)
    } finally {
      setLoading(false)
    }
  }, [id])

  // First verify access when component mounts (only for learners)
  useEffect(() => {
    if (isLearner) {
      verifyAccess()
    }
  }, [isLearner, verifyAccess])

  // Disable body scroll when access is denied
  useEffect(() => {
    if (accessDenied) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [accessDenied])

  // Load course details only after access is verified
  useEffect(() => {
    if (accessVerified) {
      loadCourseDetails()
    }
  }, [accessVerified, loadCourseDetails])

  // Fetch review stats for the course
  useEffect(() => {
    const fetchReviewStats = async () => {
      if (!id) return
      try {
        const stats = await reviewService.getCourseReviewStats(id)
        setReviewStats(stats)
      } catch (error) {
        console.error('Failed to fetch review stats:', error)
        // Set default stats on error
        setReviewStats({
          avg_rating: 0,
          total_reviews: 0,
          rating_breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        })
      }
    }

    if (accessVerified && id) {
      fetchReviewStats()
    }
  }, [accessVerified, id])

  // Fetch assessments and calculate their total duration on the frontend directly from API response
  useEffect(() => {
    const fetchAssessmentsAndCalculateDuration = async () => {
      if (!id || isScormCourse) return

      try {
        const listData = await courseService.listCourseAssessments(id)
        setAssessmentsList(listData || [])

        if (listData && listData.length > 0) {
          // Calculate total minutes directly from listData API response items
          const totalMinutes = listData.reduce((acc, item) => acc + (item.time_limit_minutes || 0), 0)

          // Load assessment details to populate assessmentsMap for pre/post cards
          const detailPromises = listData.map(item =>
            courseService.getAssessmentAdmin(item.assessment_id)
              .then(res => res?.data?.data || res?.data || res)
              .catch(err => {
                console.error(`Failed to load details for assessment ${item.assessment_id}:`, err)
                return item
              })
          )

          const detailsList = await Promise.all(detailPromises)
          const newMap: Record<string, any> = {}

          detailsList.forEach((details, index) => {
            const listItem = listData[index]
            const detailsData = details || listItem
            if (detailsData) {
              const detailsId = detailsData.id || detailsData._id || detailsData.assessment_id || listItem?.assessment_id
              if (detailsId) {
                newMap[detailsId] = detailsData
              }
            }
          })


          const hours = Math.floor(totalMinutes / 60)
          const minutes = totalMinutes % 60
          setAssessmentDuration({ hours, minutes })
        } else {
          setAssessmentDuration({ hours: 0, minutes: 0 })
          setAssessmentsList([])
        }
      } catch (error) {
        console.error('Failed to calculate assessment duration:', error)
        setAssessmentDuration({ hours: 0, minutes: 0 })
        setAssessmentsList([])
      }
    }

    if (accessVerified && id) {
      fetchAssessmentsAndCalculateDuration()
    }
  }, [accessVerified, id, isScormCourse])

  // Load content trees for all modules to display lesson-level assessments
  useEffect(() => {
    const fetchAllModuleContents = async () => {
      if (!courseData?.modules || isScormCourse) return

      try {
        const promises = courseData.modules.map(module =>
          courseService.getModuleContentTree(module._id)
            .then(content => ({ moduleId: module._id, content }))
            .catch(err => {
              console.error(`Failed to load content for module ${module._id}:`, err)
              return null
            })
        )

        const results = await Promise.all(promises)
        const newMap: Record<string, any> = {}

        results.forEach(res => {
          if (res) {
            newMap[res.moduleId] = res.content
          }
        })

        setModuleContents(newMap)
      } catch (error) {
        console.error('Failed to load all module contents:', error)
      }
    }

    if (accessVerified && courseData?.modules) {
      fetchAllModuleContents()
    }
  }, [accessVerified, courseData?.modules, isScormCourse])



  const formatDuration = (duration?: Duration | null): string => {
    if (!duration) return '0m'

    // Normalize input to total seconds rounded to the nearest integer
    let totalSeconds = Math.round(
      (duration.hours || 0) * 3600 +
      (duration.minutes || 0) * 60 +
      (duration.seconds || 0)
    )

    if (totalSeconds <= 0) return '0m'

    const hours = Math.floor(totalSeconds / 3600)
    totalSeconds %= 3600
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    const parts = []
    if (hours) parts.push(`${hours}h`)
    if (minutes) parts.push(`${minutes}m`)
    // Show seconds only under the hour scale (e.g. "2m 23s", "45s") so short
    if (!hours && seconds) parts.push(`${seconds}s`)
    return parts.join(' ') || '0m'
  }

  const isNonZeroDuration = (d?: Duration | null): boolean => {
    if (!d) return false
    return (d.hours || 0) > 0 || (d.minutes || 0) > 0 || (d.seconds || 0) > 0
  }

  const getCalculatedTotalDuration = (): Duration => {
    let totalSeconds = 0

    if (courseData?.estimated_duration?.videos) {
      const v = courseData.estimated_duration.videos
      totalSeconds += (v.hours || 0) * 3600 + (v.minutes || 0) * 60 + (v.seconds || 0)
    }

    if (courseData?.estimated_duration?.practice) {
      const p = courseData.estimated_duration.practice
      totalSeconds += (p.hours || 0) * 3600 + (p.minutes || 0) * 60 + (p.seconds || 0)
    }

    if (assessmentDuration) {
      totalSeconds += (assessmentDuration.hours || 0) * 3600 + (assessmentDuration.minutes || 0) * 60 + (assessmentDuration.seconds || 0)
    }

    // Fallback to module-level durations if totalSeconds is 0
    if (totalSeconds === 0 && courseData?.modules) {
      courseData.modules.forEach(module => {
        const hours = module.estimated_duration?.hours || 0
        const minutes = module.estimated_duration?.minutes || 0
        const seconds = module.estimated_duration?.seconds || 0
        totalSeconds += (hours * 3600) + (minutes * 60) + seconds
      })
      if (assessmentDuration) {
        totalSeconds += (assessmentDuration.hours || 0) * 3600 + (assessmentDuration.minutes || 0) * 60 + (assessmentDuration.seconds || 0)
      }
    }

    const hours = Math.floor(totalSeconds / 3600)
    const remainingSeconds = Math.round(totalSeconds % 3600)
    const minutes = Math.floor(remainingSeconds / 60)
    const seconds = remainingSeconds % 60

    return {
      hours,
      minutes,
      seconds
    }
  }

  const getTotalDuration = (): string => {
    return formatDuration(getCalculatedTotalDuration())
  }

  // const formatNumber = (num: number): string => {
  //   if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  //   if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  //   return num?.toString() || '0'
  // }

  // Calculate rating percentage for breakdown
  const getRatingPercentage = (starCount: number): number => {
    if (!reviewStats || reviewStats.total_reviews === 0) return 0
    const count = reviewStats.rating_breakdown[starCount as keyof typeof reviewStats.rating_breakdown] || 0
    return Math.round((count / reviewStats.total_reviews) * 100)
  }

  const toggleModuleExpanded = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }))
  }

  const getTotalContentItems = (): number => {
    if (courseData?.stats?.totalContentItems) {
      return courseData.stats.totalContentItems
    }
    if (!courseData?.modules) return 0
    return courseData.modules.reduce((acc, module) => {
      const stats = module.content_stats || {}
      return acc + (stats.total || 0)
    }, 0)
  }

  const getBloomLevelColor = (level: string): string => {
    const colors: { [key: string]: string } = {
      'Remember': 'bg-blue-100 text-blue-800',
      'Understand': 'bg-green-100 text-green-800',
      'Apply': 'bg-yellow-100 text-yellow-800',
      'Analyze': 'bg-orange-100 text-orange-800',
      'Evaluate': 'bg-purple-100 text-purple-800',
      'Create': 'bg-red-100 text-red-800'
    }
    return colors[level] || 'bg-gray-100 text-gray-800'
  }

  // Markdown components with custom styling
  const markdownComponents = {
    p: ({children}: MarkdownComponentProps) => <p className="text-base text-gray-700 leading-relaxed">{children}</p>,
    h1: ({children}: MarkdownComponentProps) => <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 break-words">{children}</h1>,
    h2: ({children}: MarkdownComponentProps) => <h2 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-3">{children}</h2>,
    h3: ({children}: MarkdownComponentProps) => <h3 className="text-lg font-semibold text-gray-900 mb-2">{children}</h3>,
    strong: ({children}: MarkdownComponentProps) => <strong className="font-semibold text-gray-900">{children}</strong>,
    em: ({children}: MarkdownComponentProps) => <em className="italic text-gray-700">{children}</em>,
    a: ({href, children}: MarkdownComponentProps) => <a href={href} className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
    code: ({children}: MarkdownComponentProps) => <code className="bg-gray-100 text-primary-800 px-1 py-0.5 rounded text-sm font-medium">{children}</code>,
    ul: ({children}: MarkdownComponentProps) => <ul className="list-disc list-inside my-3 space-y-1 text-gray-700">{children}</ul>,
    ol: ({children}: MarkdownComponentProps) => <ol className="list-decimal list-inside my-3 space-y-1 text-gray-700">{children}</ol>,
    li: ({children}: MarkdownComponentProps) => <li className="my-1">{children}</li>,
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'course-info':
        return (
          <div className="space-y-6 sm:space-y-8">
            {/* Course Title & Description */}
            <div>
              <h3 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 leading-snug break-words [overflow-wrap:anywhere]">{courseData?.title}</h3>
              <div className="prose prose-sm sm:prose-lg max-w-none">
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed break-words">
                  {courseData?.description || 'No description available'}
                </p>
              </div>
            </div>

            {/* Course Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-brand-blue-50 rounded-lg p-4 border border-brand-blue-200">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="w-5 h-5 text-brand-blue-600" />
                  <span className="text-sm font-medium text-brand-blue-900">Difficulty Level</span>
                </div>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {courseData?.difficulty || 'All Levels'}
                </p>
              </div>

              <div className="bg-brand-green-50 rounded-lg p-4 border border-brand-green-200">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-5 h-5 text-brand-green-600" />
                  <span className="text-sm font-medium text-brand-green-900">Category</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {courseData?.category_name || 'General'}
                </p>
              </div>

              <div className="bg-brand-purple-50 rounded-lg p-4 border border-brand-purple-200">
                <div className="flex items-center gap-3 mb-2">
                  <GraduationCap className="w-5 h-5 text-brand-purple-600" />
                  <span className="text-sm font-medium text-brand-purple-900">Language</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {courseData?.language ? getLanguageName(courseData.language) : 'English'}
                </p>
              </div>
            </div>

            {/* Course Rating & Stats */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Course Statistics</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${i < Math.floor(reviewStats?.avg_rating || 0) ? 'fill-yellow-400 text-yellow-400' : i < (reviewStats?.avg_rating || 0) ? 'fill-yellow-400/50 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{reviewStats?.avg_rating?.toFixed(1) || '0.0'}</div>
                  <div className="text-sm text-gray-600">Rating</div>
                </div>

                {!isScormCourse && (
                  <div className="text-center">
                    <Clock className="w-8 h-8 text-brand-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{getTotalDuration()}</div>
                    <div className="text-sm text-gray-600">Duration</div>
                  </div>
                )}

                <div className="text-center">
                  <Award className="w-8 h-8 text-brand-purple-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{courseData?.modules?.length || courseData?.stats?.totalModules || 0}</div>
                  <div className="text-sm text-gray-600">Modules</div>
                </div>
                
                <div className="text-center">
                  <BookOpen className="w-8 h-8 text-brand-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{courseData?.stats?.totalContentItems || 0}</div>
                  <div className="text-sm text-gray-600">Lessons</div>
                </div>
              </div>

              {/* Rating Breakdown */}
              {reviewStats && reviewStats.total_reviews > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <span className="text-lg font-semibold text-gray-900">
                      {reviewStats.avg_rating.toFixed(1)} course rating
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-sm text-gray-600">
                      {reviewStats.total_reviews} {reviewStats.total_reviews === 1 ? 'rating' : 'ratings'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const percentage = getRatingPercentage(star)
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5 w-20">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${i < star ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-yellow-400 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 w-10 text-right">{percentage}%</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      case 'overview':
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">About this Course</h3>
              <p className="text-gray-700 leading-relaxed text-lg max-w-4xl">
                {courseData?.description || 'No description available'}
              </p>
            </div>

            {/* Duration Breakdown */}
            {!isScormCourse && courseData?.estimated_duration && (
              <div>
                <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">Time Investment</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-brand-blue-50 rounded-lg p-4 border border-brand-blue-200">
                    <Clock className="w-6 h-6 text-brand-blue-600 mb-2" />
                    <div className="text-sm text-gray-600">Total Duration</div>
                    <div className="text-lg font-bold text-gray-900">
                      {formatDuration(getCalculatedTotalDuration())}
                    </div>
                  </div>
                  {isNonZeroDuration(courseData.estimated_duration.videos) && !isHtmlOrAudio && (
                    <div className="bg-brand-purple-50 rounded-lg p-4 border border-brand-purple-200">
                      <Video className="w-6 h-6 text-brand-purple-600 mb-2" />
                      <div className="text-sm text-gray-600">Video Content</div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatDuration(courseData.estimated_duration.videos)}
                      </div>
                    </div>
                  )}
                  {isNonZeroDuration(courseData.estimated_duration.practice) && (
                    <div className="bg-brand-green-50 rounded-lg p-4 border border-brand-green-200">
                      <Code className="w-6 h-6 text-brand-green-600 mb-2" />
                      <div className="text-sm text-gray-600">Practice Time</div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatDuration(courseData.estimated_duration.practice)}
                      </div>
                    </div>
                  )}
                  {isNonZeroDuration(assessmentDuration) && (
                    <div className="bg-brand-orange-50 rounded-lg p-4 border border-brand-orange-200">
                      <Trophy className="w-6 h-6 text-brand-orange-600 mb-2" />
                      <div className="text-sm text-gray-600">Assessments</div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatDuration(assessmentDuration)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Course Statistics */}
            {courseData?.stats && (
              <div>
                <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">Course Contents</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{courseData.stats.totalModules || 0}</div>
                    <div className="text-sm text-gray-600">Modules</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{courseData.stats.totalContentItems || 0}</div>
                    <div className="text-sm text-gray-600">Total Items</div>
                  </div>
                  {courseData.stats.totalVideos > 0 && !isHtmlOrAudio && (
                    <div className="text-center p-4 bg-brand-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-brand-purple-900">{courseData.stats.totalVideos}</div>
                      <div className="text-sm text-brand-purple-600">Videos</div>
                    </div>
                  )}
                  {courseData.stats.totalTexts > 0 && (
                    <div className="text-center p-4 bg-brand-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-brand-green-900">{courseData.stats.totalTexts}</div>
                      <div className="text-sm text-brand-green-600">Text Lessons</div>
                    </div>
                  )}
                  {courseData.stats.totalAudios > 0 && (
                    <div className="text-center p-4 bg-brand-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-brand-orange-900">{courseData.stats.totalAudios}</div>
                      <div className="text-sm text-brand-orange-600">Audio Content</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            {courseData?.tags && courseData.tags.length > 0 && (
              <div>
                <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">Topics Covered</h3>
                <div className="flex flex-wrap gap-2">
                  {courseData.tags.map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Target Audience */}
            {courseData?.target_audience && courseData.target_audience.length > 0 && (
              <div>
                <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">Who This Course is For</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {courseData.target_audience.map((audience, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{audience}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Prerequisites & Requirements */}
            <div>
              <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">Prerequisites & Requirements</h3>
              {courseData?.prerequisites && courseData.prerequisites.length > 0 ? (
                <div className="space-y-3">
                  {courseData.prerequisites.map((req, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                      <Shield className="w-5 h-5 text-brand-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{typeof req === 'string' ? req : JSON.stringify(req)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 sm:p-6 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">No Prerequisites Required</h4>
                      <p className="text-gray-700">This course is suitable for beginners. Just bring your enthusiasm and willingness to learn!</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      case 'curriculum':
        return (
          <div className="space-y-4">
            <div className="mb-6">
              <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 mb-2">Course Content</h3>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm text-gray-600">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  {courseData?.module_count || courseData?.modules?.length || 0} modules
                </span>
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {getTotalContentItems()} lessons
                </span>
                {!isScormCourse && (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {getTotalDuration()} total length
                  </span>
                )}
              </div>
            </div>

            {courseData?.modules && courseData.modules.length > 0 ? (
              courseData.modules.map((module) => (
                <div key={module._id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleModuleExpanded(module._id)}
                    className="w-full bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 hover:bg-gray-100 transition-colors"
                  >
                    {/* Mobile: title row, then the stats wrap underneath (indented
                        under the title) instead of crushing the title. */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-4">
                      <div className="flex items-start sm:items-center gap-3 min-w-0">
                        <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform flex-shrink-0 mt-0.5 sm:mt-0 ${expandedModules[module._id] ? 'rotate-180' : ''}`} />
                        <div className="text-left flex-1 min-w-0">
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                            Module {module.order}: {module.title}
                          </h4>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 pl-8 sm:pl-0 text-xs sm:text-sm text-gray-600 flex-shrink-0">
                        {module.content_stats && (
                          <>
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" />
                              {module.content_stats.total} lessons
                            </span>
                            {(module.content_stats.video ?? 0) > 0 && (
                              <span className="flex items-center gap-1">
                                <Video className="w-4 h-4" />
                                {module.content_stats.video}
                              </span>
                            )}
                            {(module.content_stats.text ?? 0) > 0 && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                {module.content_stats.text}
                              </span>
                            )}
                          </>
                        )}
                        {!isScormCourse && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDuration(module.estimated_duration)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  {expandedModules[module._id] && (
                    <div className="bg-white border-t">
                      {/* Module Description */}
                      {module.description && (
                        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gray-50">
                          <h5 className="text-sm font-semibold text-gray-900 mb-2">Module Overview</h5>
                          <p className="text-sm text-gray-700 leading-relaxed">{module.description}</p>
                        </div>
                      )}
                      
                      {/* Module Learning Objectives */}
                      {module.learning_objectives && module.learning_objectives.length > 0 && (
                        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-brand-blue-50/30">
                          <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Target className="w-4 h-4 text-brand-blue-600" />
                            Module Learning Objectives
                          </h5>
                          <div className="space-y-2">
                            {module.learning_objectives.map((obj) => (
                              <div key={obj._id} className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <span className="text-sm text-gray-700 block">{obj.description}</span>
                                  {obj.bloom_level && (
                                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${getBloomLevelColor(obj.bloom_level)}`}>
                                      {obj.bloom_level}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No curriculum available yet</p>
              </div>
            )}
          </div>
        )

      case 'learning-outcomes':
        return (
          <div className="space-y-8">
            {/* Learning Objectives Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Course Learning Objectives</h3>
              {courseData?.learning_objectives && courseData.learning_objectives.length > 0 ? (
                <div className="space-y-4">
                  {courseData.learning_objectives.map((objective, index) => {
                    const icons = [Target, TrendingUp, BarChart3, Zap, Award, Database, Brain, Shield]
                    const Icon = icons[index % icons.length]
                    
                    return (
                      <div key={objective._id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-primary-600" />
                        </div>
                        <div className="flex-1 max-w-4xl">
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {objective.description || ''}
                            </ReactMarkdown>
                          </div>
                          {objective.bloom_level && (
                            <span className={`inline-block mt-2 px-3 py-1 text-xs rounded-full ${getBloomLevelColor(objective.bloom_level)}`}>
                              Bloom Level: {objective.bloom_level}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No learning objectives defined yet</p>
                </div>
              )}
            </div>

            {/* Overall Course Assessments Breakdown — count summary */}
            {!isScormCourse && (() => {
              const coursePre = assessmentsList.find(a => a.assessment_level === 'COURSE' && a.assessment_type === 'PRE')
              const coursePost = assessmentsList.find(a => a.assessment_level === 'COURSE' && a.assessment_type === 'POST')

              // Module-level counts
              const modulesWithAss = (courseData?.modules || []).filter((mod: any) =>
                assessmentsList.some(a => a.assessment_level === 'MODULE' && a.module_id === mod._id)
              )
              const modulePreCount = assessmentsList.filter(a => a.assessment_level === 'MODULE' && a.assessment_type === 'PRE').length
              const modulePostCount = assessmentsList.filter(a => a.assessment_level === 'MODULE' && a.assessment_type === 'POST').length

              // Lesson-level counts (from moduleContents + CONTENT_ITEM from assessmentsList)
              let lessonPreCount = 0
              let lessonPostCount = 0
              const seenLessonIds = new Set<string>()

              Object.values(moduleContents).forEach((mc: any) => {
                ; (mc?.content_items || []).forEach((l: any) => {
                  if (l.pre_assessment?.id && !seenLessonIds.has(l.pre_assessment.id)) {
                    seenLessonIds.add(l.pre_assessment.id)
                    lessonPreCount++
                  }
                  if (l.post_assessment?.id && !seenLessonIds.has(l.post_assessment.id)) {
                    seenLessonIds.add(l.post_assessment.id)
                    lessonPostCount++
                  }
                })
              })
              assessmentsList.filter(a => a.assessment_level === 'CONTENT_ITEM').forEach(a => {
                if (!seenLessonIds.has(a.assessment_id)) {
                  seenLessonIds.add(a.assessment_id)
                  if (a.assessment_type === 'PRE') lessonPreCount++
                  else lessonPostCount++
                }
              })

              const hasAny =
                coursePre || coursePost ||
                modulesWithAss.length > 0 ||
                lessonPreCount > 0 || lessonPostCount > 0

              if (!hasAny) return null

              const StatCard = ({
                icon: Icon,
                gradient,
                borderColor,
                iconBg,
                iconColor,
                label,
                subtitle,
                pre,
                post
              }: {
                icon: any
                gradient: string
                borderColor: string
                iconBg: string
                iconColor: string
                label: string
                subtitle?: string
                pre: number
                post: number
              }) => (
                <div className={`group bg-white rounded-2xl border ${borderColor} p-5 shadow-xs hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 relative overflow-hidden flex flex-col justify-between`}>
                  {/* Gradient Top Accent Bar */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />

                  <div>
                    <div className="flex items-center gap-3.5 mb-4">
                      <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0 shadow-xs transition-transform group-hover:scale-105`}>
                        <Icon className={`w-5 h-5 ${iconColor}`} />
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-gray-900 tracking-tight">{label}</h4>
                        {subtitle && <p className="text-xs text-gray-500 font-medium mt-0.5">{subtitle}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap pt-3.5 border-t border-gray-100">
                    {pre > 0 && (
                      <div className="flex items-center gap-1.5 bg-brand-blue-50/80 border border-brand-blue-100 text-brand-blue-800 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-2xs">
                        <CheckSquare className="w-3.5 h-3.5 text-brand-blue-600 flex-shrink-0" />
                        <span>{pre} {pre === 1 ? 'Pre-Assessment' : 'Pre-Assessments'}</span>
                      </div>
                    )}
                    {post > 0 && (
                      <div className="flex items-center gap-1.5 bg-amber-50/80 border border-amber-100 text-amber-800 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-2xs">
                        <Award className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <span>{post} {post === 1 ? 'Post-Assessment' : 'Post-Assessments'}</span>
                      </div>
                    )}
                    {pre === 0 && post === 0 && (
                      <span className="text-xs font-medium text-gray-400">No active assessments</span>
                    )}
                  </div>
                </div>
              )

              const hasCourseAss = coursePre || coursePost
              const hasModuleAss = modulePreCount > 0 || modulePostCount > 0
              const hasLessonAss = lessonPreCount > 0 || lessonPostCount > 0

              const totalActiveCards = (hasCourseAss ? 1 : 0) + (hasModuleAss ? 1 : 0) + (hasLessonAss ? 1 : 0)

              if (totalActiveCards === 0) return null

              const gridColsClass =
                totalActiveCards === 1 ? 'grid-cols-1 max-w-sm' :
                  totalActiveCards === 2 ? 'grid-cols-1 md:grid-cols-2' :
                    'grid-cols-1 md:grid-cols-3'

              return (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight">Overall Course Assessments</h3>
                  <div className={`grid ${gridColsClass} gap-4`}>
                    {hasCourseAss && (
                      <StatCard
                        icon={Trophy}
                        gradient="from-brand-purple-500 to-brand-indigo-500"
                        borderColor="border-brand-purple-100"
                        iconBg="bg-brand-purple-50"
                        iconColor="text-brand-purple-600"
                        label="Course Level"
                        subtitle="Placement & final exams"
                        pre={coursePre ? 1 : 0}
                        post={coursePost ? 1 : 0}
                      />
                    )}
                    {hasModuleAss && (
                      <StatCard
                        icon={BookOpen}
                        gradient="from-brand-blue-500 to-brand-cyan-500"
                        borderColor="border-brand-blue-100"
                        iconBg="bg-brand-blue-50"
                        iconColor="text-brand-blue-600"
                        label="Module Level"
                        subtitle={`${modulesWithAss.length} ${modulesWithAss.length === 1 ? 'module has' : 'modules have'} assessments`}
                        pre={modulePreCount}
                        post={modulePostCount}
                      />
                    )}
                    {hasLessonAss && (
                      <StatCard
                        icon={FileText}
                        gradient="from-brand-emerald-500 to-brand-teal-500"
                        borderColor="border-brand-emerald-100"
                        iconBg="bg-brand-emerald-50"
                        iconColor="text-brand-emerald-600"
                        label="Lesson Level"
                        subtitle="In-lesson practice & quizzes"
                        pre={lessonPreCount}
                        post={lessonPostCount}
                      />
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        )

      case 'reviews':
        return (
          <div className="space-y-6">
            <ReviewsList
              targetId={id || ''}
              targetType={ReviewType.COURSE}
              targetTitle={courseData?.title || ''}
              showWriteReview={isLearner}
              canWriteReview={canStartLearning}
              currentUserId={user?.id}
              isAdmin={!isLearner}
            />
          </div>
        )

      default:
        return null
    }
  }

  // Show skeleton while verifying access (only for learners)
  if (isLearner && !accessVerified && !accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="space-y-4 sm:space-y-6 p-0 sm:p-4 lg:p-6">
          <SkeletonLoader type="custom" className="h-44 sm:h-64 md:h-80 lg:h-96 w-full rounded-xl" />
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
            <div className="lg:col-span-2 xl:col-span-3 2xl:col-span-4 space-y-4">
              <SkeletonLoader type="custom" className="h-12 w-full rounded-xl" />
              <SkeletonLoader type="custom" className="h-64 w-full rounded-xl" />
            </div>
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 shadow-xl space-y-4">
                <SkeletonLoader type="custom" className="h-48 w-full rounded-lg" />
                <SkeletonLoader type="custom" className="h-6 w-3/4 rounded" />
                <SkeletonLoader type="text" count={3} />
                <SkeletonLoader type="custom" className="h-12 w-full rounded-lg" />
                <SkeletonLoader type="custom" className="h-10 w-full rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show access denied UI
  if (accessDenied) {
    return (
      <div className="h-screen overflow-hidden  flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-[5rem]">
            {/* Top colored bar */}
            <div className="h-1.5 bg-gradient-to-r from-red-500 to-red-500" />

            <div className="p-4 sm:p-6">
              {/* Icon */}
              <div className="relative mx-auto w-16 h-16 mb-4">
                <div className="absolute inset-0 bg-red-100 rounded-full animate-pulse" />
                <div className="relative w-full h-full bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <Shield className="w-7 h-7 text-red-500" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
                Access Restricted
              </h1>

              {/* Description */}
              <p className="text-gray-500 text-center text-sm mb-5">
                You don't have permission to view this course. It may not be assigned to you yet.
              </p>

              {/* Info Box */}
              <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-5">
                <div className="flex gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">
                    <span className="font-medium text-red-800">Need access?</span> Contact your administrator to request access.
                  </p>
                </div>
              </div>

              {/* Button */}
              <button
                onClick={() => navigate('/learn/courses')}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium text-sm shadow-md shadow-red-500/20"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="space-y-4 sm:space-y-6 p-0 sm:p-4 lg:p-6">
          <SkeletonLoader type="custom" className="h-44 sm:h-64 md:h-80 lg:h-96 w-full rounded-xl" />
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
            <div className="lg:col-span-2 xl:col-span-3 2xl:col-span-4 space-y-4">
              <SkeletonLoader type="custom" className="h-12 w-full rounded-xl" />
              <div className="bg-white rounded-xl p-6 space-y-4">
                <SkeletonLoader type="title" />
                <SkeletonLoader type="text" count={4} />
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <SkeletonLoader type="custom" className="h-20 w-full rounded-lg" />
                  <SkeletonLoader type="custom" className="h-20 w-full rounded-lg" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 shadow-xl space-y-4">
                <SkeletonLoader type="custom" className="h-48 w-full rounded-lg" />
                <SkeletonLoader type="custom" className="h-6 w-3/4 rounded" />
                <SkeletonLoader type="text" count={3} />
                <SkeletonLoader type="custom" className="h-12 w-full rounded-lg" />
                <SkeletonLoader type="custom" className="h-10 w-full rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!courseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-24 h-24 text-gray-400 mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Course Not Available</h2>
          <p className="text-gray-600 mb-6 max-w-md">We couldn't find the course you're looking for.</p>
          <button
            onClick={() => {
              // Same logic as CourseCompletionCelebration
              if (userMode.hasBothRoles) {
                navigate(userMode.currentMode === 'admin' ? '/admin/course/course-catalog' : '/learn');
              } else {
                navigate(isLearner ? '/learn' : '/admin/course/course-catalog');
              }
            }}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {userMode.hasBothRoles 
              ? (userMode.currentMode === 'admin' ? 'Browse Courses' : 'Back to Learning Dashboard')
              : (isLearner ? 'Back to Learning Dashboard' : 'Browse Courses')
            }
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="space-y-4 sm:space-y-6 p-0 sm:p-4 lg:p-6">
        {/* Course hero — courses with no banner (SCORM imports, manual courses without an
            uploaded image) show a BookOpen placeholder hero instead. */}
        {!hasBanner ? (
          <div className="relative h-48 sm:h-64 md:h-80 lg:h-96 w-full overflow-hidden rounded-xl shadow-lg bg-gradient-to-br from-primary-100 to-primary-200 flex flex-col items-center justify-center gap-3 sm:gap-4">
            {/* Back Button */}
            <div className="absolute top-4 left-4 z-10">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 px-4 py-2 bg-black/30 backdrop-blur-sm text-white rounded-lg hover:bg-black/50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">Back to Courses</span>
              </button>
            </div>

            {!isLearner && (
              <div className="absolute top-4 right-4 z-10">
                <div className="bg-brand-purple-600 text-white px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium">Preview Mode</span>
                </div>
              </div>
            )}
            <BookOpen className="w-12 h-12 sm:w-20 sm:h-20 text-primary-500" />
            <div className="text-center px-4 sm:px-6">
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 line-clamp-2 break-words [overflow-wrap:anywhere]">{courseData.title}</h1>
              <span className={`px-4 py-2 text-sm font-semibold rounded-full ${
                courseData.difficulty === 'beginner' ? 'bg-green-500 text-white' :
                courseData.difficulty === 'intermediate' ? 'bg-yellow-500 text-white' :
                courseData.difficulty === 'advanced' ? 'bg-red-500 text-white' :
                'bg-gray-500 text-white'
              }`}>
                {courseData.difficulty || 'All Levels'}
              </span>
            </div>
          </div>
        ) : (
          <div className="relative h-44 sm:h-64 md:h-80 lg:h-96 w-full overflow-hidden bg-gray-100 rounded-xl shadow-lg">
            {/* Back Button */}
            <div className="absolute top-4 left-4 z-10">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 px-4 py-2 bg-black/30 backdrop-blur-sm text-white rounded-lg hover:bg-black/50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">Back to Courses</span>
              </button>
            </div>

            {/* Admin Preview Badge - Inside banner top-right */}
            {!isLearner && (
              <div className="absolute top-4 right-4 z-10">
                <div className="bg-brand-purple-600 text-white px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium">Preview Mode</span>
                </div>
              </div>
            )}
            <img
              src={bannerUrl}
              alt={`${courseData.title} banner`}
              className="w-full h-full object-cover rounded-xl"
              onError={() => setBannerError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6 lg:p-8">
              <div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                  <span className={`px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-semibold rounded-full backdrop-blur-sm [text-shadow:_0_1px_2px_rgb(0_0_0_/_30%)] ${
                    courseData.difficulty === 'beginner' ? 'bg-green-500/80 text-white' :
                    courseData.difficulty === 'intermediate' ? 'bg-yellow-500/80 text-white' :
                    courseData.difficulty === 'advanced' ? 'bg-red-500/80 text-white' :
                    'bg-gray-500/80 text-white'
                  }`}>
                    {courseData.difficulty || 'All Levels'}
                  </span>
                  {courseData.category_name && (
                    <span className="px-3 sm:px-4 py-1 sm:py-2 bg-white/20 backdrop-blur-sm text-white text-xs sm:text-sm rounded-full [text-shadow:_0_1px_2px_rgb(0_0_0_/_30%)]">
                      {courseData.category_name}
                    </span>
                  )}
                  <div className="flex items-center gap-2 text-white [text-shadow:_0_1px_2px_rgb(0_0_0_/_30%)]">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm font-medium">{getTotalDuration()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Below lg the action card (with Start Learning) stacks under all the
            tab content, so show the primary action right under the banner. It
            scrolls with the page (not a fixed bar). */}
        <div className="lg:hidden bg-white rounded-xl shadow-sm p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 sm:flex-1 min-w-0">
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-primary-500" />
              {courseData.module_count || courseData.modules?.length || 0} modules
            </span>
            <span className="flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-primary-500" />
              {courseData.stats?.totalContentItems || 0} items
            </span>
            {!isScormCourse && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-primary-500" />
                {getTotalDuration()}
              </span>
            )}
          </div>
          <button
            onClick={() => canStartLearning && navigate(isLearner ? `/learn/study/${id}` : `/admin/ai-learn/${id}`)}
            disabled={!canStartLearning}
            title={!canStartLearning ? 'You do not have permission to start learning' : undefined}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
              canStartLearning
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-primary-600 text-white opacity-50 cursor-not-allowed'
            }`}
          >
            <PlayCircle className="w-5 h-5" />
            Start Learning
          </button>
        </div>

        {/* Main Content Grid - Side by side layout */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
            {/* Left Content - Tabs Section (responsive width for better use of space) */}
            <div className="lg:col-span-2 xl:col-span-3 2xl:col-span-4">
              {/* Tabs Navigation */}
              <div className="bg-white rounded-xl shadow-sm">
                <div className="border-b border-gray-200">
                  <div className="px-3 sm:px-6 overflow-x-auto">
                    <div className="flex space-x-5 sm:space-x-8">
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-1.5 sm:gap-2 py-3 sm:py-4 border-b-2 font-medium text-sm transition-all whitespace-nowrap ${
                            activeTab === tab.id
                              ? 'border-primary-500 text-primary-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <tab.icon className="w-4 h-4" />
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-4 sm:p-6">
                  {renderTabContent()}
                </div>
              </div>
            </div>

            {/* Right Content - Action Card (responsive width) */}
            <div className="lg:col-span-1 xl:col-span-1 2xl:col-span-1">
              <div className="bg-white rounded-xl p-4 sm:p-6 text-gray-900 shadow-xl lg:sticky lg:top-24">
                {(!isScormCourse && hasThumbnail) ? (
                  <div className="relative overflow-hidden rounded-lg mb-4">
                    <img
                      src={thumbnailUrl}
                      alt={courseData.title}
                      onError={() => setThumbnailError(true)}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg mb-4 flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-primary-600" />
                  </div>
                )}
                
                {!isLearner && (
                  <div className="text-center mb-6">
                    <div className="space-y-1">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl font-bold text-gray-900">Preview Mode</span>
                      </div>
                      <p className="text-sm text-gray-600">This is how learners will see your course</p>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <button
                    onClick={() => canStartLearning && navigate(isLearner ? `/learn/study/${id}` : `/admin/ai-learn/${id}`)}
                    disabled={!canStartLearning}
                    title={!canStartLearning ? 'You do not have permission to start learning' : undefined}
                    className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                      canStartLearning
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-primary-600 text-white opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <PlayCircle className="w-5 h-5" />
                    Start Learning
                  </button>
                  {!isLearner ? (
                    <button 
                      onClick={() => navigate(`/admin/edit-course/${id}`)}
                      className="w-full px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Back to Edit Mode
                    </button>
                  ) : (
                    <button 
                      onClick={() => navigate('/learn/courses')}
                      className="w-full px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Back to My Courses
                    </button>
                  )}
                </div>

                {/* Course Stats */}
                <div className="mt-6 pt-6 border-t space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Modules</span>
                    <span className="font-semibold">{courseData.module_count || courseData.modules?.length || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Items</span>
                    <span className="font-semibold">{courseData.stats?.totalContentItems || 0}</span>
                  </div>
                  {!isScormCourse && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-semibold">{getTotalDuration()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Language</span>
                    <span className="font-semibold">{courseData.language ? getLanguageName(courseData.language) : 'English'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}